import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  ArrowRight, Users, BookOpen, TrendingUp, Search,
  Clock, Star, X, FileText, Play, ExternalLink,
  ChevronRight, Flame, Globe
} from 'lucide-react';
import { useArticles } from '../hooks/useArticles';
import ContentRow from '../components/ContentRow';

/* ─────────────────────────────────────────────
   GLOBAL STYLES + FONT INJECTION
───────────────────────────────────────────── */
const FontStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@700;900&display=swap');

    @font-face {
      font-family: 'Japanese3017';
      src: url('/fonts/Japanese-3017.ttf') format('truetype');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }

    @font-face {
      font-family: 'Acknowledgement';
      src: url('/fonts/Acknowledgement.otf') format('opentype');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }

    @font-face {
      font-family: 'Azonix';
      src: url('/fonts/Azonix.otf') format('opentype'),
           url('/fonts/azonix.woff2') format('woff2'),
           url('/fonts/azonix.woff') format('woff');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }

    :root {
      --accent-east: #dc2626;
      --accent-east-glow: rgba(249, 22, 22, 0.35);
      --accent-west: #3b82f6;
      --accent-west-glow: rgba(59,130,246,0.35);
      --accent-neutral: #a78bfa;
      --glass-bg: rgba(255,255,255,0.04);
      --glass-border: rgba(255,255,255,0.08);
      --font-azonix: 'Azonix', 'Impact', sans-serif;
      --font-japanese: 'Japanese3017', 'Noto Serif JP', 'Hiragino Mincho Pro', serif;
      --font-west: 'Acknowledgement', 'Azonix', 'Impact', sans-serif;
      --font-body: 'Georgia', serif;
      --font-ui: -apple-system, 'Helvetica Neue', sans-serif;
    }

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    .or-root {
      background: #08080c;
      color: #fff;
      font-family: var(--font-ui);
      overflow-x: hidden;
      min-height: 100vh;
      position: relative;
    }

    /* ──────────────── HERO ──────────────── */
    .or-hero {
      position: relative;
      width: 100%;
      height: 100svh;
      min-height: 560px;
      overflow: hidden;
      display: flex;
      align-items: flex-end;
    }
    .or-hero-bg {
      position: absolute; inset: 0;
      width: 100%; height: 100%;
      object-fit: cover;
      object-position: center top;
    }
    .or-hero-overlay-lr {
      position: absolute; inset: 0; z-index: 2;
      background: linear-gradient(
        to right,
        rgba(8,8,12,0.96) 0%,
        rgba(8,8,12,0.65) 42%,
        rgba(8,8,12,0.12) 72%,
        rgba(8,8,12,0.04) 100%
      );
    }
    .or-hero-overlay-bt {
      position: absolute; inset: 0; z-index: 2;
      background: linear-gradient(
        to top,
        rgba(8,8,12,1) 0%,
        rgba(8,8,12,0.5) 28%,
        transparent 58%
      );
    }
    .or-hero-body {
      position: relative; z-index: 4;
      width: 100%;
      padding: 0 clamp(20px,5vw,60px) clamp(48px,7vh,80px);
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 28px;
    }
    .or-hero-left { max-width: 560px; flex: 1; }
    .or-hero-badge {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 5px 14px; border-radius: 100px;
      background: rgba(167,139,250,0.12);
      border: 1px solid rgba(167,139,250,0.25);
      margin-bottom: 20px;
      font-size: 10px; color: var(--accent-neutral);
      letter-spacing: 2.5px; text-transform: uppercase; font-weight: 600;
      font-family: var(--font-ui);
      margin-top: 80px;
    }
    .or-hero-title {
      font-family: var(--font-azonix);
      font-size: clamp(40px, 7vw, 96px);
      line-height: 0.93;
      letter-spacing: -1.5px;
      color: #fff;
      margin-bottom: 16px;
    }
    .or-hero-title-dim { color: rgba(255,255,255,0.16); }
    .or-hero-accent-line {
      display: flex; align-items: center; gap: 12px;
      margin-bottom: 16px;
    }
    .or-hero-accent-bar {
      height: 2px; border-radius: 1px;
      transition: all 0.5s ease;
    }
    .or-hero-accent-label {
      font-family: var(--font-azonix);
      font-size: 10px; letter-spacing: 3px; text-transform: uppercase;
      transition: color 0.5s ease;
    }
    .or-hero-desc {
      font-family: var(--font-ui);
      font-size: clamp(14px,1.7vw,17px);
      color: rgba(255,255,255,0.42);
      line-height: 1.85; max-width: 430px;
      margin-bottom: 30px;
    }
    .or-hero-btns { display: flex; gap: 10px; flex-wrap: wrap; }
    .or-hero-btn {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 12px 22px; border-radius: 10px; text-decoration: none;
      font-size: 11px; font-weight: 700; letter-spacing: 0.8px;
      transition: all 0.25s ease; white-space: nowrap;
      font-family: var(--font-azonix);
    }
    .or-hero-btn-east {
      background: linear-gradient(135deg,#f97316,#ea580c); color:#fff;
      box-shadow: 0 8px 28px rgba(249,115,22,0.38);
    }
    .or-hero-btn-east:hover { box-shadow:0 14px 40px rgba(249,115,22,0.6); transform:translateY(-2px); }
    .or-hero-btn-west {
      background: linear-gradient(135deg,#3b82f6,#2563eb); color:#fff;
      box-shadow: 0 8px 28px rgba(59,130,246,0.38);
    }
    .or-hero-btn-west:hover { box-shadow:0 14px 40px rgba(59,130,246,0.6); transform:translateY(-2px); }

    /* thumbnail strip */
    .or-thumb-strip { display:flex; gap:10px; align-items:flex-end; flex-shrink:0; }
    .or-thumb {
      width: 72px; height: 108px;
      border-radius: 10px; overflow: hidden;
      border: 1px solid rgba(255,255,255,0.1);
      cursor: pointer; flex-shrink: 0;
      transition: all 0.35s ease;
      position: relative;
    }
    .or-thumb.or-thumb-active {
      width: 148px; height: 210px;
      border-color: rgba(255,255,255,0.28);
      box-shadow: 0 20px 60px rgba(0,0,0,0.65);
    }
    .or-thumb img { width:100%; height:100%; object-fit:cover; transition:transform 0.5s; }
    .or-thumb:hover img { transform:scale(1.06); }
    .or-thumb-label {
      position:absolute; bottom:0; left:0; right:0;
      padding: 22px 8px 7px;
      background: linear-gradient(to top,rgba(0,0,0,0.8),transparent);
      font-family: var(--font-azonix);
      font-size: 8px; letter-spacing:2.5px;
      opacity: 0; transition: opacity 0.3s;
    }
    .or-thumb.or-thumb-active .or-thumb-label { opacity: 1; }

    /* scroll indicator */
    .or-scroll-cue {
      position:absolute; bottom:22px; left:50%;
      transform:translateX(-50%); z-index:4;
      display:flex; flex-direction:column; align-items:center; gap:4px;
    }

    /* ──────────────── LAYOUT ──────────────── */
    .or-container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 clamp(16px,4vw,48px);
      position: relative; z-index: 1;
    }
    .or-section   { padding-bottom: clamp(56px,8vh,96px); }
    .or-section-sm { padding-bottom: clamp(36px,5vh,64px); }

    /* ──────────────── SECTION LABEL ──────────────── */
    .or-label {
      display:flex; align-items:center; gap:12px;
      margin-bottom: 24px;
    }
    .or-label-bar { width:3px; border-radius:2px; height:26px; }
    .or-label-text {
      font-family: var(--font-azonix);
      font-size: 16px; letter-spacing: 1px; text-transform:uppercase;
    }
    .or-section-head {
      display:flex; align-items:center; justify-content:space-between;
      margin-bottom: 22px;
    }
    .or-view-all {
      display:flex; align-items:center; gap:5px;
      font-size:10px; color:rgba(255,255,255,0.28);
      text-decoration:none; letter-spacing:1px;
      transition: color 0.2s; font-family: var(--font-ui);
    }
    .or-view-all:hover { color:#fff; }

    /* ──────────────── PORTAL GRID ──────────────── */
    .or-portal-grid {
      display:grid; grid-template-columns:repeat(2,1fr); gap:14px;
    }
    @media(max-width:720px){ .or-portal-grid{grid-template-columns:1fr;} }

    .or-portal-card {
      position:relative; border-radius:22px; overflow:hidden;
      cursor:pointer; display:block; text-decoration:none;
      background: var(--glass-bg);
      backdrop-filter:blur(24px); -webkit-backdrop-filter:blur(24px);
      border:1px solid var(--glass-border);
      transition:all 0.4s cubic-bezier(0.4,0,0.2,1);
      padding: clamp(22px,3vw,32px);
    }
    .or-portal-card:hover { transform:translateY(-6px); }
    .or-portal-east:hover {
      border-color:rgba(249,115,22,0.28);
      box-shadow:0 28px 80px rgba(249,115,22,0.13),0 0 0 1px rgba(249,115,22,0.1);
    }
    .or-portal-west:hover {
      border-color:rgba(59,130,246,0.28);
      box-shadow:0 28px 80px rgba(59,130,246,0.13),0 0 0 1px rgba(59,130,246,0.1);
    }
    .or-portal-title-east {
      font-family: var(--font-japanese);
      font-size: clamp(34px,5vw,58px);
      font-weight:900; color:var(--accent-east);
      text-shadow:0 0 40px var(--accent-east-glow);
      line-height:1;
    }
    .or-portal-title-west {
      font-family: var(--font-west);
      font-size: clamp(34px,5vw,58px);
      color:var(--accent-west);
      text-shadow:0 0 40px var(--accent-west-glow);
      line-height:1;
    }
    .or-stats-grid {
      display:grid; grid-template-columns:repeat(3,1fr);
      gap:1px; background:rgba(255,255,255,0.06);
      border-radius:11px; overflow:hidden; margin-bottom:18px;
    }
    .or-stat-cell {
      padding:11px 8px; text-align:center;
      background:rgba(0,0,0,0.28);
    }
    .or-portal-cta {
      display:flex; align-items:center; justify-content:center; gap:8px;
      padding:12px; border-radius:11px;
      font-family:var(--font-azonix); font-size:10px;
      letter-spacing:2px; text-transform:uppercase;
    }

    /* ──────────────── FEATURE LAYOUT ──────────────── */
    .or-feature-grid {
      display:grid; grid-template-columns:1fr 288px;
      gap:12px; align-items:start;
    }
    .or-feature-grid-rev { grid-template-columns:288px 1fr; }
    @media(max-width:880px){
      .or-feature-grid, .or-feature-grid-rev { grid-template-columns:1fr; }
    }
    .or-side-col { display:flex; flex-direction:column; gap:9px; }

    .or-art-grid {
      display:grid; gap:10px;
      grid-template-columns:repeat(4,1fr);
      margin-top:10px;
    }
    @media(max-width:880px){ .or-art-grid{grid-template-columns:repeat(2,1fr);} }
    @media(max-width:480px){ .or-art-grid{grid-template-columns:1fr;} }

    .or-browse-grid {
      display:grid; gap:10px;
      grid-template-columns:repeat(4,1fr);
    }
    @media(max-width:1024px){ .or-browse-grid{grid-template-columns:repeat(3,1fr);} }
    @media(max-width:680px){ .or-browse-grid{grid-template-columns:repeat(2,1fr);} }
    @media(max-width:400px){ .or-browse-grid{grid-template-columns:1fr;} }

    .or-search-grid {
      display:grid; gap:14px;
      grid-template-columns:repeat(3,1fr);
    }
    @media(max-width:780px){ .or-search-grid{grid-template-columns:repeat(2,1fr);} }
    @media(max-width:480px){ .or-search-grid{grid-template-columns:1fr;} }

    /* ──────────────── HERO ARTICLE ──────────────── */
    .or-hero-art {
      position:relative; border-radius:18px; overflow:hidden;
      height:clamp(280px,38vw,460px);
      display:block; text-decoration:none; cursor:pointer;
    }
    .or-hero-art img {
      position:absolute; inset:0;
      width:100%; height:100%; object-fit:cover;
      transition:transform 0.7s ease;
    }
    .or-hero-art:hover img { transform:scale(1.04); }
    .or-hero-art-grad {
      position:absolute; inset:0;
      background:linear-gradient(to top,rgba(0,0,0,0.92) 0%,rgba(0,0,0,0.3) 50%,transparent 100%);
    }
    .or-hero-art-body {
      position:absolute; bottom:0; left:0; right:0;
      padding: clamp(16px,2.5vw,24px);
    }
    .or-cat-tag {
      font-family:var(--font-azonix);
      font-size:9px; font-weight:700;
      letter-spacing:2.5px; text-transform:uppercase;
      margin-bottom:8px; display:inline-block;
    }
    .or-hero-art-title {
      font-family:var(--font-azonix);
      font-size:clamp(14px,2.5vw,22px);
      line-height:1.2; color:#fff;
      display:-webkit-box; -webkit-line-clamp:2;
      -webkit-box-orient:vertical; overflow:hidden;
      margin-bottom:10px;
    }
    .or-art-meta {
      display:flex; gap:12px; align-items:center;
      font-size:10px; color:rgba(255,255,255,0.38);
      font-family:var(--font-ui);
    }
    .or-art-meta span { display:flex; align-items:center; gap:4px; }

    /* ──────────────── SIDE CARD ──────────────── */
    .or-side-card {
      display:flex; border-radius:12px; overflow:hidden;
      background:var(--glass-bg); border:1px solid var(--glass-border);
      text-decoration:none;
      backdrop-filter:blur(20px); -webkit-backdrop-filter:blur(20px);
      transition:all 0.28s ease;
    }
    .or-side-card:hover {
      background:rgba(255,255,255,0.07);
      border-color:rgba(255,255,255,0.14);
      transform:translateX(4px);
    }
    .or-side-thumb {
      width:88px; min-width:88px; height:74px;
      position:relative; overflow:hidden; flex-shrink:0;
    }
    .or-side-thumb img { width:100%; height:100%; object-fit:cover; }
    .or-side-rank {
      position:absolute; top:5px; left:5px;
      width:18px; height:18px; border-radius:50%;
      display:flex; align-items:center; justify-content:center;
      font-size:8px; font-weight:800; color:#fff;
      font-family:var(--font-azonix);
    }
    .or-side-body { padding:9px 12px; flex:1; overflow:hidden; }
    .or-side-title {
      font-size:11px; font-weight:600; color:#fff; line-height:1.4;
      display:-webkit-box; -webkit-line-clamp:2;
      -webkit-box-orient:vertical; overflow:hidden;
      font-family:var(--font-ui); margin-bottom:3px;
    }

    /* ──────────────── GRID CARD ──────────────── */
    .or-grid-card {
      border-radius:13px; overflow:hidden;
      background:var(--glass-bg); border:1px solid var(--glass-border);
      text-decoration:none; display:block;
      backdrop-filter:blur(16px); -webkit-backdrop-filter:blur(16px);
      transition:all 0.28s ease;
    }
    .or-grid-card:hover {
      background:rgba(255,255,255,0.07);
      border-color:rgba(255,255,255,0.14);
      transform:translateY(-4px);
      box-shadow:0 18px 56px rgba(0,0,0,0.42);
    }
    .or-grid-card-img {
      position:relative; aspect-ratio:16/9; overflow:hidden;
    }
    .or-grid-card-img img {
      width:100%; height:100%; object-fit:cover;
      transition:transform 0.5s ease;
    }
    .or-grid-card:hover .or-grid-card-img img { transform:scale(1.06); }
    .or-grid-card-body { padding:11px 13px 13px; }
    .or-grid-card-title {
      font-size:12px; font-weight:600; color:#fff; line-height:1.4;
      display:-webkit-box; -webkit-line-clamp:2;
      -webkit-box-orient:vertical; overflow:hidden;
      font-family:var(--font-ui);
    }

    /* ──────────────── DIVIDER ──────────────── */
    .or-divider {
      height:1px; margin-bottom: clamp(48px,7vh,80px);
      background:linear-gradient(to right,transparent,rgba(255,255,255,0.06),transparent);
    }

    /* ──────────────── CTA BOX ──────────────── */
    .or-cta {
      max-width:680px; margin:0 auto;
      border-radius:22px; text-align:center;
      position:relative; overflow:hidden;
      background:var(--glass-bg);
      backdrop-filter:blur(24px); -webkit-backdrop-filter:blur(24px);
      border:1px solid var(--glass-border);
      padding: clamp(32px,5vw,60px) clamp(22px,4vw,40px);
    }
    .or-cta-glow {
      position:absolute; top:50%; left:50%; transform:translate(-50%,-50%);
      width:340px; height:170px; border-radius:50%;
      background:radial-gradient(ellipse,rgba(167,139,250,0.1),transparent);
      filter:blur(40px); pointer-events:none;
    }
    .or-cta-link {
      display:inline-flex; align-items:center; gap:8px;
      padding:10px 24px; border-radius:10px; text-decoration:none;
      border:1px solid rgba(167,139,250,0.3);
      color:var(--accent-neutral); font-size:11px; font-weight:600;
      letter-spacing:1px; transition:all 0.25s;
      font-family:var(--font-azonix);
    }
    .or-cta-link:hover {
      border-color:rgba(167,139,250,0.55);
      background:rgba(167,139,250,0.08);
    }

    /* ──────────────── SEARCH MODAL ──────────────── */
    .or-search-overlay {
      position:fixed; inset:0; z-index:200;
      display:flex; align-items:flex-start; justify-content:center;
      padding:72px 16px 0;
    }
    .or-search-drop {
      position:absolute; inset:0;
      background:rgba(0,0,0,0.75);
      backdrop-filter:blur(14px); -webkit-backdrop-filter:blur(14px);
    }
    .or-search-box {
      position:relative; width:100%; max-width:580px;
      background:rgba(16,16,22,0.95);
      border:1px solid rgba(255,255,255,0.1);
      border-radius:16px; overflow:hidden;
      backdrop-filter:blur(24px); -webkit-backdrop-filter:blur(24px);
    }
    .or-search-row {
      display:flex; align-items:center; gap:10px;
      padding:14px 16px;
      border-bottom:1px solid rgba(255,255,255,0.06);
    }
    .or-search-input {
      flex:1; background:transparent; border:none; outline:none;
      color:#fff; font-size:15px; font-family:var(--font-ui);
    }
    .or-search-input::placeholder { color:rgba(255,255,255,0.22); }
    .or-search-close {
      background:rgba(255,255,255,0.06); border:none;
      border-radius:7px; padding:5px; cursor:pointer;
      display:flex; align-items:center;
    }

    /* ──────────────── FLOATING SEARCH BTN ──────────────── */
    .or-fab {
      position:fixed; bottom:26px; right:26px; z-index:50;
      width:46px; height:46px; border-radius:50%;
      background:rgba(255,255,255,0.06);
      backdrop-filter:blur(16px); -webkit-backdrop-filter:blur(16px);
      border:1px solid rgba(255,255,255,0.1);
      display:flex; align-items:center; justify-content:center;
      cursor:pointer; box-shadow:0 8px 30px rgba(0,0,0,0.4);
      transition:all 0.25s;
    }
    .or-fab:hover {
      background:rgba(255,255,255,0.12);
      transform:scale(1.08);
    }

    /* hero responsive */
    @media(max-width:768px){
      .or-thumb-strip { display:none; }
      .or-hero-overlay-lr {
        background:linear-gradient(to top,rgba(8,8,12,0.98) 0%,rgba(8,8,12,0.55) 50%,rgba(8,8,12,0.18) 100%);
      }
    }
    
    /* ──────────────── OUTRANGE PORTAL SECTION ──────────────── */
    .or-outrange-portal-section {
      margin-top:20px;
    }
    .or-outrange-portal-card {
      position:relative; border-radius:20px; overflow:hidden;
      aspect-ratio:21/9; min-height:280px;
      display:flex; align-items:center; justify-content:center;
      cursor:pointer; transition:transform .35s ease;
    }
    .or-outrange-portal-card:hover { transform:scale(1.02); }
    .or-outrange-portal-img {
      position:absolute; inset:0; width:100%; height:100%; object-fit:cover;
    }
    .or-outrange-portal-grad {
      position:absolute; inset:0;
      background:linear-gradient(to right,rgba(8,8,12,.92) 0%,rgba(8,8,12,.65) 40%,rgba(8,8,12,.3) 100%);
    }
    .or-outrange-portal-content {
      position:relative; z-index:2; text-align:center; padding:30px;
    }
    .or-outrange-portal-label {
      font-family:var(--font-azonix); font-size:10px; letter-spacing:3px;
      color:rgba(255,255,255,.5); text-transform:uppercase;
      margin-bottom:12px;
    }
    .or-outrange-portal-title {
      font-family:var(--font-azonix); font-size:clamp(24px,4vw,36px);
      letter-spacing:1px; margin-bottom:16px;
    }
    .or-outrange-portal-btn {
      display:inline-flex; align-items:center; gap:8px;
      padding:12px 28px; border-radius:10px;
      background:rgba(167,139,250,.15); border:1px solid rgba(167,139,250,.25);
      color:#a78bfa; font-family:var(--font-azonix); font-size:10px;
      letter-spacing:1.5px; text-transform:uppercase;
      backdrop-filter:blur(10px); transition:all .25s;
    }
    .or-outrange-portal-card:hover .or-outrange-portal-btn {
      background:rgba(167,139,250,.25); border-color:rgba(167,139,250,.4);
    }
    @media(max-width:640px){
      .or-outrange-portal-card { aspect-ratio:4/3; min-height:240px; }
      .or-outrange-portal-grad { background:linear-gradient(to top,rgba(8,8,12,.95) 0%,rgba(8,8,12,.5) 50%,rgba(8,8,12,.2) 100%); }
    }
  `}</style>
);

/* ─────────────────────────────────────────────
   PARTICLE BACKGROUND
───────────────────────────────────────────── */
const ParticleBackground = () => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W = canvas.width = window.innerWidth;
    let H = canvas.height = window.innerHeight;

    const resize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
    window.addEventListener('resize', resize);

    const particles = Array.from({ length: 52 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.27, vy: (Math.random() - 0.5) * 0.27,
      r: Math.random() * 1.5 + 0.3,
      alpha: Math.random() * 0.42 + 0.08,
      hue: [220, 255, 200][Math.floor(Math.random() * 3)],
    }));

    const onMouse = (e) => { mouseRef.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener('mousemove', onMouse);

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const { x: mx, y: my } = mouseRef.current;

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x, dy = particles[i].y - particles[j].y;
          const d = Math.hypot(dx, dy);
          if (d < 115) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(100,118,255,${0.07 * (1 - d / 115)})`;
            ctx.lineWidth = 0.45;
            ctx.stroke();
          }
        }
      }

      particles.forEach((p) => {
        const dx = mx - p.x, dy = my - p.y, d = Math.hypot(dx, dy);
        if (d < 105) { p.vx -= (dx / d) * 0.033; p.vy -= (dy / d) * 0.033; }
        p.vx *= 0.99; p.vy *= 0.99;
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.hue},72%,70%,${p.alpha})`;
        ctx.fill();
      });

      const g = ctx.createRadialGradient(mx, my, 0, mx, my, 175);
      g.addColorStop(0, 'rgba(99,102,241,0.05)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouse);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.65 }} />;
};

/* ─────────────────────────────────────────────
   HERO SECTION  (streaming-style, full viewport)
───────────────────────────────────────────── */
const heroSlides = [
  { src: '/assets/east-poster-animac-website.jpg', label: 'EAST', link: '/outrange/east', color: '#f97316', sub: 'Anime & Manga' },
  { src: '/assets/west-poster-animac-website.jpg', label: 'WEST', link: '/outrange/west', color: '#3b82f6', sub: 'Movies & Cartoons' },
  { src: '/assets/animac-neutral-poster.jpg',       label: 'HUB',  link: '/outrange',      color: '#a78bfa', sub: 'All Content' },
];

const HeroSection = () => {
  const [active, setActive] = useState(0);
  const [transitioning, setTransitioning] = useState(false);

  useEffect(() => {
    const t = setInterval(() => cycle(active), 5500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const cycle = (cur) => {
    setTransitioning(true);
    setTimeout(() => { setActive((cur + 1) % heroSlides.length); setTransitioning(false); }, 380);
  };

  const switchTo = (i) => {
    if (i === active || transitioning) return;
    setTransitioning(true);
    setTimeout(() => { setActive(i); setTransitioning(false); }, 340);
  };

  const s = heroSlides[active];

  return (
    <div className="or-hero">
      {/* BG image */}
      <AnimatePresence mode="sync">
        <motion.img
          key={active} src={s.src} alt="" className="or-hero-bg"
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7 }}
        />
      </AnimatePresence>

      {/* Accent color tint */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1,
        background: `radial-gradient(ellipse at 68% 38%, ${s.color}14 0%, transparent 55%)`,
        transition: 'background 0.8s',
      }} />

      <div className="or-hero-overlay-lr" />
      <div className="or-hero-overlay-bt" />

      {/* Main content */}
      <div className="or-hero-body">
        <div className="or-hero-left">
          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
            <div className="or-hero-badge">
              <Flame size={10} />
              Where Culture Meets Commentary
            </div>

            <h1 className="or-hero-title" style={{ opacity: transitioning ? 0 : 1, transition: 'opacity 0.32s' }}>
              OUTRANGE
              <br />
              <span className="or-hero-title-dim">HUB</span>
            </h1>

            <div className="or-hero-accent-line">
              <div className="or-hero-accent-bar" style={{ width: '28px', background: s.color }} />
              <span className="or-hero-accent-label" style={{ color: s.color }}>{s.sub}</span>
            </div>

            <p className="or-hero-desc">
              Two worlds. Infinite stories. Anime culture from the East, blockbuster entertainment from the West — curated for the culture.
            </p>

            <div className="or-hero-btns">
              <Link to="/outrange/east" className="or-hero-btn or-hero-btn-east">
                East Portal <ChevronRight size={14} />
              </Link>
              <Link to="/outrange/west" className="or-hero-btn or-hero-btn-west">
                West Portal <ChevronRight size={14} />
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Thumbnail strip */}
        <div className="or-thumb-strip">
          {heroSlides.map((sl, i) => (
            <div
              key={i}
              className={`or-thumb${i === active ? ' or-thumb-active' : ''}`}
              onClick={() => switchTo(i)}
              style={{ borderColor: i === active ? `${sl.color}55` : 'rgba(255,255,255,0.1)' }}
            >
              <img src={sl.src} alt={sl.label} />
              <div className="or-thumb-label" style={{ color: sl.color }}>{sl.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll cue */}
      <div className="or-scroll-cue">
        <motion.div animate={{ opacity: [0.3, 0.75, 0.3] }} transition={{ repeat: Infinity, duration: 2.2 }}>
          <div style={{ width: '1px', height: '34px', background: 'linear-gradient(to bottom, rgba(255,255,255,0.28), transparent)', margin: '0 auto' }} />
          <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'rgba(255,255,255,0.28)', margin: '4px auto 0' }} />
        </motion.div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   SHARED PRIMITIVES
───────────────────────────────────────────── */
const SectionLabel = ({ children, accent = '#a78bfa', icon: Icon = Globe }) => (
  <div className="or-label">
    <div className="or-label-bar" style={{ background: `linear-gradient(to bottom, ${accent}, transparent)` }} />
    <Icon size={12} color={accent} />
    <span className="or-label-text" style={{ color: accent }}>{children}</span>
  </div>
);

const SectionHeader = ({ label, accent, icon, linkTo }) => (
  <div className="or-section-head">
    <SectionLabel accent={accent} icon={icon}>{label}</SectionLabel>
    {linkTo && (
      <Link to={linkTo} className="or-view-all">
        View All <ExternalLink size={10} />
      </Link>
    )}
  </div>
);

const accent = (cat) => cat === 'east' ? '#f97316' : cat === 'west' ? '#3b82f6' : '#a78bfa';

/* ─────────────────────────────────────────────
   PORTAL CARD
───────────────────────────────────────────── */
const PortalCard = ({ portal, index }) => {
  const isEast = portal.id === 'east';
  const ac = isEast ? '#f97316' : '#3b82f6';

  return (
    <motion.div initial={{ opacity: 0, y: 36 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: index * 0.14 }}>
      <Link to={portal.path} className={`or-portal-card ${isEast ? 'or-portal-east' : 'or-portal-west'}`}>
        {/* Background Image */}
        <div 
          style={{ 
            position: 'absolute', 
            inset: 0, 
            borderRadius: '22px',
            backgroundImage: isEast 
              ? "url('https://i.pinimg.com/1200x/2d/50/93/2d509399aceab6e117c84583a5b1679f.jpg')" 
              : "url('https://i.pinimg.com/1200x/32/a7/dc/32a7dc5ab50029a87761aec1ad3533ae.jpg')",
            backgroundSize: 'cover', 
            backgroundPosition: 'center',
            pointerEvents: 'none',
          }} 
        />
        {/* Black Gradient Overlay - Left to Right */}
        <div style={{ 
          position: 'absolute', 
          inset: 0, 
          borderRadius: '22px',
          background: 'linear-gradient(to right, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.75) 50%, rgba(0,0,0,0.4) 100%)',
          pointerEvents: 'none',
        }} />
        {/* tint */}
        <div style={{ position: 'absolute', inset: 0, borderRadius: '22px', pointerEvents: 'none', background: isEast ? 'linear-gradient(135deg,rgba(249,115,22,0.07) 0%,transparent 58%)' : 'linear-gradient(135deg,rgba(59,130,246,0.07) 0%,transparent 58%)' }} />
        {/* top line */}
        <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: '1px', background: `linear-gradient(to right,transparent,${ac},transparent)`, opacity: 0.45 }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
            <div>
              <div style={{ fontSize: '8px', letterSpacing: '3px', color: 'rgba(255,255,255,0.28)', textTransform: 'uppercase', marginBottom: '5px', fontFamily: 'var(--font-ui)' }}>Portal</div>
              <h2 className={isEast ? 'or-portal-title-east' : 'or-portal-title-west'}>{portal.title}</h2>
              <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.42)', marginTop: '3px', fontFamily: 'var(--font-ui)' }}>{portal.subtitle}</p>
            </div>
            <div style={{ width: '38px', height: '38px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.04)', flexShrink: 0 }}>
              <ArrowRight size={14} color={ac} />
            </div>
          </div>

          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.36)', lineHeight: 1.72, marginBottom: '22px', fontFamily: 'var(--font-ui)' }}>
            {portal.description}
          </p>

          <div className="or-stats-grid">
            {[
              { Ic: BookOpen, val: portal.stats.articles, lbl: 'Articles' },
              { Ic: Users,    val: portal.stats.readers,  lbl: 'Readers' },
              { Ic: TrendingUp, val: portal.stats.trending, lbl: 'Trending' },
            ].map((s, i) => (
              <div className="or-stat-cell" key={i}>
                <s.Ic size={12} color={ac} style={{ margin: '0 auto 4px', display: 'block' }} />
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#fff', fontFamily: 'var(--font-azonix)' }}>{s.val}</div>
                <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.28)', letterSpacing: '1px', textTransform: 'uppercase', fontFamily: 'var(--font-ui)' }}>{s.lbl}</div>
              </div>
            ))}
          </div>

          <div className="or-portal-cta" style={{ background: `linear-gradient(135deg,${ac}18,${ac}0c)`, border: `1px solid ${ac}32`, color: ac }}>
            Enter {portal.title} <ChevronRight size={12} />
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────
   ARTICLE CARDS
───────────────────────────────────────────── */
const HeroArticleCard = ({ article, category, delay = 0 }) => {
  if (!article) return null;
  const ac = accent(category);
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay }}>
      <Link to={`/article/${article.slug}`} className="or-hero-art">
        {article.featured_image
          ? <img src={article.featured_image} alt={article.title} />
          : <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg,#0f0f1a,#1a1a2e)' }} />
        }
        <div className="or-hero-art-grad" />
        <div style={{ position: 'absolute', top: 14, left: 14 }}>
          <div style={{ background: ac, color: '#fff', fontSize: '8px', fontWeight: 700, letterSpacing: '2px', padding: '4px 11px', borderRadius: '100px', textTransform: 'uppercase', fontFamily: 'var(--font-azonix)', boxShadow: `0 0 14px ${ac}55` }}>
            Featured
          </div>
        </div>
        <div className="or-hero-art-body">
          <div className="or-cat-tag" style={{ color: ac }}>{article.category || category}</div>
          <h3 className="or-hero-art-title">{article.title}</h3>
          {article.excerpt && (
            <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.42)', marginBottom: '10px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontFamily: 'var(--font-body)', lineHeight: 1.6 }}>
              {article.excerpt}
            </p>
          )}
          <div className="or-art-meta">
            {article.author && <span><Users size={10} />{article.author}</span>}
            <span><Clock size={10} />{article.read_time || 5} min</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const SideArticleCard = ({ article, rank, category, delay = 0 }) => {
  if (!article) return null;
  const ac = accent(category);
  return (
    <motion.div initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.42, delay }}>
      <Link to={`/article/${article.slug}`} className="or-side-card">
        <div className="or-side-thumb">
          {article.featured_image
            ? <img src={article.featured_image} alt={article.title} />
            : <div style={{ width: '100%', height: '100%', background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FileText size={13} color="#444" /></div>
          }
          {rank && <div className="or-side-rank" style={{ background: ac }}>{rank}</div>}
        </div>
        <div className="or-side-body">
          <div style={{ fontSize: '8px', color: ac, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '3px', fontWeight: 700, fontFamily: 'var(--font-azonix)' }}>
            {article.category || category}
          </div>
          <p className="or-side-title">{article.title}</p>
          <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.28)', display: 'flex', alignItems: 'center', gap: '3px', fontFamily: 'var(--font-ui)' }}>
            <Clock size={8} />{article.read_time || 5} min
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const GridArticleCard = ({ article, category, delay = 0 }) => {
  if (!article) return null;
  const ac = accent(category || article?.category);
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.42, delay }}>
      <Link to={`/article/${article.slug}`} className="or-grid-card">
        <div className="or-grid-card-img">
          {article.featured_image
            ? <img src={article.featured_image} alt={article.title} />
            : <div style={{ width: '100%', aspectRatio: '16/9', background: `${ac}14`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Play size={18} color={ac} opacity={0.4} /></div>
          }
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(0,0,0,0.42),transparent)' }} />
        </div>
        <div className="or-grid-card-body">
          <div style={{ fontSize: '8px', color: ac, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '5px', fontWeight: 700, fontFamily: 'var(--font-azonix)' }}>
            {article.category || category}
          </div>
          <p className="or-grid-card-title">{article.title}</p>
          <div style={{ marginTop: '7px', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '9px', color: 'rgba(255,255,255,0.28)', fontFamily: 'var(--font-ui)' }}>
            <Clock size={8} />{article.read_time || 5} min
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────
   SEARCH MODAL
───────────────────────────────────────────── */
const SearchModal = ({ isOpen, onClose, onSearch }) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { if (isOpen && inputRef.current) inputRef.current.focus(); }, [isOpen]);
  const handleSubmit = (e) => { e.preventDefault(); if (query.trim()) { onSearch(query); onClose(); } };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div className="or-search-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
          <div className="or-search-drop" />
          <motion.div className="or-search-box"
            initial={{ y: -14, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: -14, opacity: 0 }}
            onClick={e => e.stopPropagation()}
          >
            <form onSubmit={handleSubmit} className="or-search-row">
              <Search size={16} color="rgba(255,255,255,0.32)" />
              <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)}
                placeholder="Search articles, topics, creators…" className="or-search-input" />
              <button type="button" onClick={onClose} className="or-search-close">
                <X size={14} color="rgba(255,255,255,0.42)" />
              </button>
            </form>
            <div style={{ padding: '16px 18px' }}>
              <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.2)', fontFamily: 'var(--font-ui)' }}>
                {query ? `Press Enter to search "${query}"` : 'Type to search across all articles'}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ─────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────── */
const Outrange = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search');
  const [searchOpen, setSearchOpen] = useState(false);

  const { articles: eastArticles } = useArticles('east');
  const { articles: westArticles } = useArticles('west');
  const { articles: allArticles } = useArticles();
  const { articles: searchResults, loading: searchLoading } = useArticles(null, null, 50, 0, true, searchQuery);

  const handleSearch = (q) => navigate(`/outrange?search=${encodeURIComponent(q)}`);

  const portalData = [
    {
      id: 'east', title: 'EAST', subtitle: 'Anime & Manga Culture',
      description: 'Deep-dive features, trending news, and sharp analysis from the world of anime, manga, and Japanese pop culture.',
      path: '/outrange/east', articles: eastArticles,
      stats: { articles: eastArticles?.length || 0, readers: '125K', trending: 'One Piece' },
    },
    {
      id: 'west', title: 'WEST', subtitle: 'Movies & Cartoons',
      description: 'Hollywood blockbusters, indie animations, and western cartoon classics covered with a sharp editorial perspective.',
      path: '/outrange/west', articles: westArticles,
      stats: { articles: westArticles?.length || 0, readers: '98K', trending: 'Spider-Verse' },
    },
  ];

  return (
    <>
      <FontStyles />
      <SEO
        title="Outrange Hub"
        description="Latest viral trends, news, and entertainment from Outrange Hub."
        url={typeof window !== 'undefined' ? window.location.href : '/outrange'}
        image="/assets/Outrange black.png"
        type="website"
      />

      <ParticleBackground />
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} onSearch={handleSearch} />

      {/* Floating search FAB */}
      <motion.button
        className="or-fab"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2 }}
        onClick={() => setSearchOpen(true)}
        aria-label="Search"
      >
        <Search size={17} color="rgba(255,255,255,0.68)" />
      </motion.button>

      <div className="or-root">

        {/* ── HERO ── */}
        <HeroSection />

        <div className="or-container">

          {/* ── SEARCH RESULTS ── */}
          {searchQuery ? (
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={{ paddingTop: '48px', paddingBottom: '80px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '28px' }}>
                <Search size={16} color="#a78bfa" />
                <h2 style={{ fontFamily: 'var(--font-azonix)', fontSize: 'clamp(16px,3vw,24px)', color: '#fff' }}>
                  Results for <span style={{ color: '#a78bfa' }}>"{searchQuery}"</span>
                </h2>
              </div>
              {searchLoading ? (
                <div style={{ textAlign: 'center', padding: '60px', color: 'rgba(255,255,255,0.28)' }}>Searching…</div>
              ) : searchResults.length > 0 ? (
                <div className="or-search-grid">
                  {searchResults.map((a) => <GridArticleCard key={a.id} article={a} category={a.category} />)}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '60px' }}>
                  <p style={{ color: 'rgba(255,255,255,0.28)', marginBottom: '16px' }}>No results for "{searchQuery}"</p>
                  <Link to="/outrange" style={{ color: '#a78bfa', textDecoration: 'none', fontSize: '12px', fontFamily: 'var(--font-azonix)' }}>← Back to Hub</Link>
                </div>
              )}
            </motion.div>
          ) : (
            <>
              {/* ── PORTALS ── */}
              <section className="or-section" style={{ paddingTop: 'clamp(44px,6vh,72px)' }}>
                <SectionLabel accent="#a78bfa" icon={Globe}>Choose Your Portal</SectionLabel>
                <div className="or-portal-grid">
                  {portalData.map((p, i) => <PortalCard key={p.id} portal={p} index={i} />)}
                </div>
              </section>

              {/* ── EAST ── */}
              {eastArticles && eastArticles.length > 0 && (
                <section className="or-section">
                  <SectionHeader label="East · Anime & Manga" accent="#f97316" icon={Flame} linkTo="/outrange/east" />
                  <div className="or-feature-grid">
                    <HeroArticleCard article={eastArticles[0]} category="east" />
                    <div className="or-side-col">
                      {eastArticles.slice(1, 5).map((a, i) => (
                        <SideArticleCard key={a.id || i} article={a} rank={i + 1} category="east" delay={i * 0.07} />
                      ))}
                    </div>
                  </div>
                  {eastArticles.length > 5 && (
                    <div className="or-art-grid">
                      {eastArticles.slice(5, 9).map((a, i) => (
                        <GridArticleCard key={a.id || i} article={a} category="east" delay={i * 0.05} />
                      ))}
                    </div>
                  )}
                </section>
              )}

              <div className="or-divider" />

              {/* ── WEST ── */}
              {westArticles && westArticles.length > 0 && (
                <section className="or-section">
                  <SectionHeader label="West · Movies & Cartoons" accent="#3b82f6" icon={Star} linkTo="/outrange/west" />
                  <div className="or-feature-grid or-feature-grid-rev">
                    <div className="or-side-col">
                      {westArticles.slice(1, 5).map((a, i) => (
                        <SideArticleCard key={a.id || i} article={a} rank={i + 1} category="west" delay={i * 0.07} />
                      ))}
                    </div>
                    <HeroArticleCard article={westArticles[0]} category="west" />
                  </div>
                  {westArticles.length > 5 && (
                    <div className="or-art-grid">
                      {westArticles.slice(5, 9).map((a, i) => (
                        <GridArticleCard key={a.id || i} article={a} category="west" delay={i * 0.05} />
                      ))}
                    </div>
                  )}
                </section>
              )}

              <div className="or-divider" />

              {/* ── BROWSE ALL ── */}
              {allArticles && allArticles.length > 0 && (
                <section className="or-section">
                  <SectionLabel accent="#a78bfa" icon={TrendingUp}>Everything · Browse All</SectionLabel>
                  <div className="or-browse-grid">
                    {allArticles.slice(0, 12).map((a, i) => (
                      <GridArticleCard key={a.id || i} article={a} category={a.category} delay={i * 0.03} />
                    ))}
                  </div>
                </section>
              )}

              {/* ── CONTENT ROWS (preserved) ── */}
              {eastArticles && eastArticles.length > 0 && (
                <section className="or-section-sm">
                  <SectionLabel accent="#f97316" icon={BookOpen}>This Week in Anime</SectionLabel>
                  <ContentRow articles={eastArticles} category="east"
                    emptyMessage="Discover the latest anime releases, manga chapters, and Japanese entertainment news." />
                </section>
              )}
              {westArticles && westArticles.length > 0 && (
                <section className="or-section-sm">
                  <SectionLabel accent="#3b82f6" icon={Play}>Movies & Cartoons Spotlight</SectionLabel>
                  <ContentRow articles={westArticles} category="west"
                    emptyMessage="Explore Hollywood Blockbusters, Disney animations, and streaming series reviews." />
                </section>
              )}

              {/* ── OUTRANGE PORTAL ── */}
              <section className="or-section">
                <div className="or-outrange-portal-section">
                  <div className="or-outrange-portal-card">
                    <img className="or-outrange-portal-img" src="https://i.pinimg.com/1200x/17/cf/72/17cf72663ad6e3d1c23db253b01fe6dd.jpg" alt="Outrange" />
                    <div className="or-outrange-portal-grad" />
                    <div className="or-outrange-portal-content">
                      <div className="or-outrange-portal-label">Discover More News and Media With</div>
                      <h2 className="or-outrange-portal-title">Outrange</h2>
                      <button className="or-outrange-portal-btn">
                        Coming Soon <ChevronRight size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              </section>

              {/* ── CTA ── */}
              <motion.section
                initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="or-section"
              >
                <div className="or-cta">
                  <div className="or-cta-glow" />
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{ fontSize: '26px', marginBottom: '12px' }}>✦</div>
                    <h3 style={{ fontFamily: 'var(--font-azonix)', fontSize: 'clamp(18px,3.5vw,30px)', color: '#fff', marginBottom: '10px' }}>
                      Can't Choose a Portal?
                    </h3>
                    <p style={{ fontSize: 'clamp(12px,1.4vw,14px)', color: 'rgba(255,255,255,0.36)', lineHeight: 1.8, fontFamily: 'var(--font-body)', maxWidth: '440px', margin: '0 auto 26px' }}>
                      Explore both worlds and discover the full spectrum of entertainment culture — from Tokyo to Hollywood.
                    </p>
                    <Link to="/" className="or-cta-link">
                      Return to Homepage <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
              </motion.section>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Outrange;